/* eslint-disable arrow-body-style */

import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { useMemoizedFn } from 'ahooks';
import TriggerViewCard from '@gp/components/TriggerViewCard';
import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
import Table from '@gp/components/GeneralTable';
import Filter from './components/Filter';
import useDownload, { createDownloadService } from '@gp/hooks/useDownload';
import { FILE_TYPES2, FILE_TYPES_MAP } from '@gp/constants/const';

import type { IBaseProps } from '@home/types/common';
import type { SortBy } from 'tea-component/lib/table/addons';
import type { FilterItemType } from '@gp/components/GeneralSelectFilter';
import type { TableListOptionsType } from '@gp/components/GeneralTable/index.d';

import './index.scss';

interface IPerformanceCardProps extends IBaseProps {
  tableClassName: string;
  title: JSX.Element | null;
  filterLoading: boolean;
  filterSelectYM?: {
    yoy?: boolean;
    mom?: boolean;
  };
  filterSelectValues?: {
    project: string[];
    studio: string[];
  };
  filterOptions: FilterItemType[];
  filterChange?: (studio: string[], project: string[], ym: string[], handleFilterName: string) => void;
  tableLoading: boolean;
  tableOptions: TableListOptionsType | null;
  tableRecords: any; // 数组每一项要有uniqueId
  onTableSort: ((v: SortBy<any>) => void) | null;
  downloadParams: {
    select_id: string[];
    yoy: boolean;
    mom: boolean;
    order: 'desc' | 'asc' | undefined;
    order_metric: string;
  } | null;
  downloadServices: any;
  downloadLogKey: {
    csv: string;
    excel: string;
  } | null;
}

const DEFAULT_FILTER_SELECT_YM = {
  yoy: true,
  mom: false,
};

const DEFAULT_FILTER_SELECT_VALUES = {
  project: [],
  studio: [],
};

const DEFAULT_FILTER_OPTIONS: FilterItemType[] = [];

const PerformanceCard: React.FC<IPerformanceCardProps> = (props) => {
  const {
    style,
    className,
    tableClassName = '',
    title = null,
    filterLoading = true,
    filterSelectYM = DEFAULT_FILTER_SELECT_YM,
    filterSelectValues = DEFAULT_FILTER_SELECT_VALUES,
    filterOptions = DEFAULT_FILTER_OPTIONS,
    filterChange,
    tableLoading = true,
    tableOptions = null,
    tableRecords = null,
    onTableSort = null,
    downloadParams = null,
    downloadServices = null,
    downloadLogKey = null,
  } = props;

  // 表格内部虚拟loading
  const [tableVirtualLoading, setTableVirtualLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);

  const handleVirtualLoading = useMemoizedFn((v: boolean) => {
    setTableVirtualLoading(v || false);
  });

  const handlePagingChange = useMemoizedFn((v: any) => {
    setPageIndex(v?.pageIndex || 1);
  });

  const { isDownloading, handleDownload } = useDownload({
    csvDownloadLogKey: downloadLogKey?.csv || '',
    excelDownloadLogKey: downloadLogKey?.excel || '',
    downloadService: createDownloadService(downloadServices),
  });

  const tabs = useMemo(() => {
    return [
      {
        id: 'download',
        label: (
          <GeneralDownloadBtn
            uiType="2"
            fileTypes={FILE_TYPES2}
            isLoading={isDownloading}
            onConfirm={(payload) => {
              const fileType = payload?.fileType || FILE_TYPES_MAP.CSV;
              handleDownload({
                ...downloadParams,
                fileType,
                file_type: Number.parseInt(fileType, 10),
              });
            }}
          />
        ),
        component: (
          <div
            className={classnames('dbg-studio-monthly-report-performance-card-body', {
              'dbg-studio-list-first-line':
                !tableLoading && !tableVirtualLoading && tableRecords && tableRecords?.length && pageIndex < 2,
            })}
          >
            <Filter
              selectYM={filterSelectYM}
              selectValues={filterSelectValues}
              options={filterOptions}
              onChange={filterChange}
            />
            <Table
              className={tableClassName}
              loading={tableLoading}
              isTableBodyMaxH={false}
              tableOptions={tableOptions}
              tableRecords={tableRecords}
              onSort={onTableSort}
              onPagingChange={handlePagingChange}
              onVirtualLoading={handleVirtualLoading}
            />
          </div>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterLoading,
    tableClassName,
    tableLoading,
    tableVirtualLoading,
    pageIndex,
    isDownloading,
    downloadParams,
    handleDownload,
  ]);

  return (
    <TriggerViewCard
      style={style}
      className={classnames('dbg-studio-monthly-report-performance-card', className)}
      title={title}
      loading={false}
      isNoData={false}
      tabs={tabs}
      defaultActiveId="download"
    />
  );
};

export default React.memo(PerformanceCard);
